import { useState, useCallback } from 'react';
import { localDayKey } from '@/utils/dates';
import { supabase } from '../lib/supabase';

export type SubscriptionType = 'free' | 'trip_pass' | 'monthly' | 'yearly';

export interface SubscriptionStatus {
  type: SubscriptionType;
  startedAt: string | null;
  expiresAt: string | null;
  pausedUntil: string | null;
  /** True when getSubscription just auto-downgraded an expired paid plan to free */
  justExpired?: boolean;
}

const PAID_TYPES: ReadonlyArray<SubscriptionType> = ['trip_pass', 'monthly', 'yearly'];

/**
 * Synchronous, snapshot-based premium check.
 *
 * Mirrors tripboom-website/src/pages/Trips.tsx (subscription detection),
 * and the async `isPremium` in this file. A user is premium when:
 *   - they're on a paid plan type (trip_pass / monthly / yearly), AND
 *   - the paid period has NOT yet expired, OR they're currently paused,
 *     UNLESS the pause began after the paid period already expired
 *     (a pause does not extend an already-expired subscription).
 *
 * Use this from render paths where the subscription fields are already
 * loaded into local state — calling the async `isPremium` from a render
 * is wrong (it triggers a fetch on every render).
 */
export function isPremiumActive(
  type: SubscriptionType | null | undefined,
  expiresAt: string | null | undefined,
  pausedUntil: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!type || !PAID_TYPES.includes(type)) return false;
  const expiry = expiresAt ? new Date(expiresAt) : null;
  const pause = pausedUntil ? new Date(pausedUntil) : null;
  const notExpired = expiry ? expiry > now : false;
  const isPaused = pause ? pause > now : false;
  const pastExpiry = expiry ? expiry < now : true;
  return (notExpired || isPaused) && !(isPaused && pastExpiry);
}

export function useSubscription() {
  const [loading, setLoading] = useState(false);

  const getSubscription = useCallback(async (userId: string): Promise<SubscriptionStatus> => {
    try {
      if (!supabase) return { type: 'free', startedAt: null, expiresAt: null, pausedUntil: null };
      const { data, error } = await supabase
        .from('user_settings')
        .select('subscription_type, subscription_started_at, subscription_expires_at, subscription_paused_until')
        .eq('user_id', userId)
        .maybeSingle();

      if (error || !data) {
        return { type: 'free', startedAt: null, expiresAt: null, pausedUntil: null };
      }

      // Check expiry — but don't downgrade during an active pause
      if ((data.subscription_type === 'trip_pass' || data.subscription_type === 'monthly' || data.subscription_type === 'yearly') && data.subscription_expires_at) {
        const expiresAt = new Date(data.subscription_expires_at);
        const now = new Date();
        const isPaused = data.subscription_paused_until && new Date(data.subscription_paused_until) > now;

        if (expiresAt < now && !isPaused) {
          // Paid period ended and not paused: the server downgrades to free,
          // keeping expires_at / started_at for the pause-resume UI (#67).
          await supabase.rpc('expire_subscription_if_lapsed');
          return {
            type: 'free',
            startedAt: data.subscription_started_at,
            expiresAt: data.subscription_expires_at,
            pausedUntil: null,
            justExpired: true
          };
        }
        // If paused and past expiry, subscription is frozen — remains on the paid type
        // but isPremium will check pausedUntil to determine if features are active
      }

      return {
        type: (data.subscription_type as SubscriptionType) || 'free',
        startedAt: data.subscription_started_at,
        expiresAt: data.subscription_expires_at,
        pausedUntil: data.subscription_paused_until ?? null
      };
    } catch {
      return { type: 'free', startedAt: null, expiresAt: null, pausedUntil: null };
    }
  }, []);

  const isPremium = useCallback(async (userId: string): Promise<boolean> => {
    const subscription = await getSubscription(userId);
    const isPaidType = subscription.type === 'trip_pass' || subscription.type === 'monthly' || subscription.type === 'yearly';
    if (!isPaidType) return false;

    // If subscription is paused AND the paid period has expired, features are frozen
    if (subscription.pausedUntil) {
      const now = new Date();
      const pausedUntil = new Date(subscription.pausedUntil);
      const expiresAt = subscription.expiresAt ? new Date(subscription.expiresAt) : null;

      if (pausedUntil > now && expiresAt && expiresAt < now) {
        // In the pause window but past the paid period — not premium
        return false;
      }
    }

    return true;
  }, [getSubscription]);

  const upgradeSubscription = useCallback(async (
    _userId: string,
    plan: 'trip_pass' | 'monthly' | 'yearly'
  ): Promise<boolean> => {
    setLoading(true);
    try {
      if (!supabase) return false;
      // The server checks the beta rule and sets the plan on the caller's own
      // row; users can no longer write subscription columns directly (#67).
      const { data, error } = await supabase.rpc('beta_upgrade', { p_plan: plan });
      if (error) {
        console.error('Error upgrading subscription:', error);
        return false;
      }
      return data === true;
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelSubscription = useCallback(async (_userId: string): Promise<boolean> => {
    setLoading(true);
    try {
      if (!supabase) return false;
      const { data, error } = await supabase.rpc('cancel_subscription');
      if (error) {
        console.error('Error canceling subscription:', error);
        return false;
      }
      return data === true;
    } catch (err) {
      console.error('Error canceling subscription:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const pauseSubscription = useCallback(async (_userId: string, months: 1 | 2 | 3 | 'indefinite'): Promise<boolean> => {
    setLoading(true);
    try {
      if (!supabase) return false;
      const { data, error } = await supabase.rpc('pause_subscription', {
        p_months: months === 'indefinite' ? null : months,
      });
      if (error) {
        console.error('Error pausing subscription:', error);
        return false;
      }
      return data === true;
    } catch (err) {
      console.error('Error pausing subscription:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const resumeSubscription = useCallback(async (_userId: string): Promise<boolean> => {
    setLoading(true);
    try {
      if (!supabase) return false;
      const { data, error } = await supabase.rpc('resume_subscription');
      if (error) {
        console.error('Error resuming subscription:', error);
        return false;
      }
      return data === true;
    } catch (err) {
      console.error('Error resuming subscription:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const getGroupCounts = useCallback(async (userId: string): Promise<{ dayGroups: number; locationGroups: number }> => {
    try {
      if (!supabase) return { dayGroups: 0, locationGroups: 0 };
      const { data: itineraries, error } = await supabase
        .from('itineraries')
        .select('location, created_at')
        .eq('user_id', userId);

      if (error || !itineraries) {
        return { dayGroups: 0, locationGroups: 0 };
      }

      const uniqueLocations = new Set(
        itineraries.map(i => i.location.trim().toLowerCase())
      );

      const uniqueDates = new Set(
        itineraries.map(i => {
          const date = new Date(i.created_at);
          return localDayKey(date);
        })
      );

      return {
        dayGroups: uniqueDates.size,
        locationGroups: uniqueLocations.size
      };
    } catch {
      return { dayGroups: 0, locationGroups: 0 };
    }
  }, []);

  const wouldExceedLimit = useCallback(async (
    userId: string,
    newLocation: string,
    maxFreeDayGroups: number,
    maxFreeLocationGroups: number
  ): Promise<{ exceeds: boolean; dayGroups: number; locationGroups: number }> => {
    const isPremiumUser = await isPremium(userId);
    if (isPremiumUser) {
      return { exceeds: false, dayGroups: 0, locationGroups: 0 };
    }

    const { dayGroups, locationGroups } = await getGroupCounts(userId);

    if (!supabase) return { exceeds: false, dayGroups, locationGroups };
    const { data: itineraries } = await supabase
      .from('itineraries')
      .select('location, created_at')
      .eq('user_id', userId);

    if (!itineraries) {
      return { exceeds: false, dayGroups, locationGroups };
    }

    const normalizedNewLocation = newLocation.trim().toLowerCase();
    const existingLocations = new Set(itineraries.map(i => i.location.trim().toLowerCase()));
    const isNewLocation = !existingLocations.has(normalizedNewLocation);

    const todayKey = (() => {
      const date = new Date();
      return localDayKey(date);
    })();
    const existingDates = new Set(itineraries.map(i => {
      const date = new Date(i.created_at);
      return localDayKey(date);
    }));
    const isNewDay = !existingDates.has(todayKey);

    const wouldExceedDays = isNewDay && dayGroups >= maxFreeDayGroups;
    const wouldExceedLocations = isNewLocation && locationGroups >= maxFreeLocationGroups;

    return {
      exceeds: wouldExceedDays || wouldExceedLocations,
      dayGroups,
      locationGroups
    };
  }, [isPremium, getGroupCounts]);

  return {
    loading,
    getSubscription,
    isPremium,
    upgradeSubscription,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    getGroupCounts,
    wouldExceedLimit
  };
}
